import type { DirectiveBinding, ObjectDirective } from 'vue';

type IgnoreItem = string | Element;

export type ClickOutsideValue =
  | ((e: Event) => void)
  | {
      handler: (e: Event) => void;
      ignore?: IgnoreItem | IgnoreItem[];
      escape?: boolean;
      events?: Array<'pointerdown' | 'mousedown' | 'touchstart' | 'click'>;
    };

type ExtendedEl = HTMLElement & {
  __clickOutsideCleanup__?: () => void;
};

function asArray<T>(v: T | T[] | undefined): T[] {
  if (!v) return [];
  return Array.isArray(v) ? v : [v];
}

function resolveIgnoredElements(ignore: IgnoreItem[], root: Document): Element[] {
  const out: Element[] = [];
  for (const item of ignore) {
    if (typeof item === 'string') out.push(...Array.from(root.querySelectorAll(item)));
    else if (item instanceof Element) out.push(item);
  }
  return out;
}

function isInside(target: EventTarget | null, el: Element | null): boolean {
  if (!target || !(target instanceof Node) || !el) return false;
  return el === target || el.contains(target);
}

function normalize(binding: DirectiveBinding<ClickOutsideValue>): {
  handler: (e: Event) => void;
  ignore: IgnoreItem[];
  escape: boolean;
  events: Array<'pointerdown' | 'mousedown' | 'touchstart' | 'click'>;
} {
  const v = binding.value;

  if (typeof v === 'function') {
    return {
      handler: v,
      ignore: [],
      escape: false,
      events: ['pointerdown', 'click'],
    };
  }

  if (!v || typeof v.handler !== 'function') {
    throw new Error('v-click-outside expects a function or { handler }');
  }

  return {
    handler: v.handler,
    ignore: asArray(v.ignore),
    escape: !!v.escape,
    events: v.events?.length ? v.events : ['pointerdown', 'click'],
  };
}

function bind(el: ExtendedEl, binding: DirectiveBinding<ClickOutsideValue>) {
  // Cleanup previous listeners if any (important for updated())
  el.__clickOutsideCleanup__?.();

  const doc = el.ownerDocument;
  const { handler, ignore, escape, events } = normalize(binding);

  const onDocEvent = (e: Event) => {
    const target = e.target;

    // inside bound element -> ignore
    if (isInside(target, el)) return;

    // inside ignored elements -> ignore
    if (ignore.length) {
      const ignoredEls = resolveIgnoredElements(ignore, doc);
      for (const ig of ignoredEls) {
        if (isInside(target, ig)) return;
      }
    }

    handler(e);
  };

  const onKeydown = (e: KeyboardEvent) => {
    if (escape && e.key === 'Escape') handler(e);
  };

  // Capture phase for reliability with stopPropagation / teleports
  for (const ev of events) {
    doc.addEventListener(ev, onDocEvent, true);
  }
  if (escape) {
    doc.addEventListener('keydown', onKeydown, true);
  }

  el.__clickOutsideCleanup__ = () => {
    for (const ev of events) {
      doc.removeEventListener(ev, onDocEvent, true);
    }
    if (escape) {
      doc.removeEventListener('keydown', onKeydown, true);
    }
  };
}

export const vClickOutside: ObjectDirective<ExtendedEl, ClickOutsideValue> = {
  mounted(el, binding) {
    bind(el, binding);
  },
  updated(el, binding) {
    bind(el, binding);
  },
  unmounted(el) {
    el.__clickOutsideCleanup__?.();
    delete el.__clickOutsideCleanup__;
  },
};

export default vClickOutside;
